import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import { BookmarkSimple as BookmarkIcon } from '@phosphor-icons/react/dist/ssr/BookmarkSimple'
import { Sparkle as SparkleIcon } from '@phosphor-icons/react/dist/ssr/Sparkle'
import { X as XIcon } from '@phosphor-icons/react/dist/ssr/X'

/** Shared readable size for suggested prompts and the question composer. */
export const promptTextSx = {
	fontSize: '0.875rem',
	lineHeight: 1.45,
	letterSpacing: '-0.02em',
} as const

/** Listening state ring for voice-input buttons. */
export const micPulseSx = {
	animation: 'micPulse 1.5s infinite',
	'@keyframes micPulse': {
		'0%, 100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
		'50%': { boxShadow: '0 0 0 8px rgba(211, 47, 47, 0)' },
	},
	'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const

interface PromptRowProps {
	label: string
	saved: boolean
	disabled?: boolean
	index?: number
	onSelect: (prompt: string) => void
	onToggleSave: () => void
}

export function PromptRow({ label, saved, disabled, index = 0, onSelect, onToggleSave }: PromptRowProps) {
	return (
		<Box
			onClick={() => {
				if (!disabled) onSelect(label)
			}}
			onKeyDown={(event) => {
				if (disabled || event.target !== event.currentTarget) return
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault()
					onSelect(label)
				}
			}}
			role="button"
			sx={{
				...promptTextSx,
				alignItems: 'center',
				// transitions.dev "texts reveal" stagger: 500ms, 12px rise,
				// 3px blur, 40ms between rows.
				animation: 'promptRowIn 500ms cubic-bezier(0.22, 1, 0.36, 1) both',
				animationDelay: `${index * 40}ms`,
				borderRadius: '8px',
				color: 'text.secondary',
				cursor: disabled ? 'default' : 'pointer',
				display: 'flex',
				gap: 1.25,
				px: 1,
				py: 0.5,
				textAlign: 'left',
				width: '100%',
				'@keyframes promptRowIn': {
					from: { opacity: 0, transform: 'translateY(12px)', filter: 'blur(3px)' },
					to: { opacity: 1, transform: 'translateY(0)', filter: 'blur(0)' },
				},
				'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
				'&:hover': disabled ? undefined : { bgcolor: 'action.hover', color: 'text.primary' },
				'@media (hover: hover)': {
					'& .prompt-row-action': { opacity: 0 },
					'&:hover .prompt-row-action, &:focus-within .prompt-row-action': { opacity: 1 },
				},
				'&:focus-visible': {
					borderRadius: '6px',
					outline: '2px solid',
					outlineColor: 'primary.main',
					outlineOffset: 2,
				},
			}}
			tabIndex={disabled ? -1 : 0}
		>
			<Box
				component="span"
				// Remount on save-state change so the icon replays its pop-in.
				key={saved ? 'saved' : 'suggested'}
				sx={{
					display: 'inline-flex',
					flexShrink: 0,
					color: saved ? 'warning.main' : 'info.main',
					animation: 'promptIconIn 200ms cubic-bezier(0.22, 1, 0.36, 1) both',
					'@keyframes promptIconIn': {
						from: { opacity: 0, transform: 'scale(0.5)' },
						to: { opacity: 1, transform: 'scale(1)' },
					},
					'@media (prefers-reduced-motion: reduce)': { animation: 'none' },
				}}
			>
				{saved ? <BookmarkIcon size={16} weight="fill" /> : <SparkleIcon size={16} weight="fill" />}
			</Box>
			<Box component="span" sx={{ flex: 1 }}>
				{label}
			</Box>
			<Tooltip title={saved ? 'Remove saved prompt' : 'Save prompt'}>
				<IconButton
					aria-label={saved ? 'Remove saved prompt' : 'Save prompt'}
					className="prompt-row-action"
					disabled={disabled}
					onClick={(event) => {
						event.stopPropagation()
						event.currentTarget.blur()
						onToggleSave()
					}}
					size="small"
					sx={{ color: 'text.secondary', flexShrink: 0, transition: 'opacity 150ms' }}
				>
					{saved ? <XIcon size={14} /> : <BookmarkIcon size={14} />}
				</IconButton>
			</Tooltip>
		</Box>
	)
}
